import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * InstitutionDistributionChart (Bare) - Chart only, no wrapper
 * For embedding in narrative HTML templates
 */
function InstitutionDistributionChart({ data }) {
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data)) return []
    return data.sort((a, b) => b.value - a.value)
  }, [data])

  const total = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  )

  const chartOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: params => {
          const percentage = ((params.value / total) * 100).toFixed(1)
          return `<strong>${params.name}</strong><br/>
                  ${params.value} institutions (${percentage}%)`
        },
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
      },
      series: [
        {
          name: 'Institution Type',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: true,
            formatter: params => {
              const percentage = ((params.value / total) * 100).toFixed(1)
              return `{b|${params.name}}\n{c|${params.value}} ({per|${percentage}%})`
            },
            rich: {
              b: {
                fontSize: 14,
                fontWeight: 'bold',
                lineHeight: 20,
              },
              c: {
                fontSize: 12,
                lineHeight: 18,
              },
              per: {
                fontSize: 12,
                color: '#999',
              },
            },
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
            },
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          data: chartData,
          color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'],
        },
      ],
    }),
    [chartData, total]
  )

  if (!data || chartData.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No institution data available
      </div>
    )
  }

  return <ReactECharts option={chartOptions} style={{ height: '400px' }} />
}

InstitutionDistributionChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ),
}

export default InstitutionDistributionChart
